import { FC, useEffect, useState } from 'react';
import { z } from 'zod';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

import { graphql } from '../../gql';
import { useModalContext } from '../../modal/ModalContext';
import { Button, FormHelperText, IconButton, InputLabel, OutlinedInput, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { ArrowBack, Close } from '@mui/icons-material';
import { useApiMutation, useApiQuery } from '../../query/hooks';
import { CreateJourneyTemplates } from './CreateJourneyTemplates';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useNavigate } from 'react-router';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { useSnackbar } from '../../store/snackbar';

const journey_createJourneyFromTemplate = graphql(`
	mutation journey_createJourneyFromTemplate($journeyId: UUID!, $name: String!) {
		createJourneyDeepClone(journeyId: $journeyId, name: $name) {
			ok
			errorMessage
			node {
				id
			}
		}
	}
`);

const journey_createJourney = graphql(`
	mutation journey_createJourney($name: String!) {
		createJourney(data: { name: $name }) {
			ok
			errorMessage
			node {
				id
			}
		}
	}
`);

const journeyCreateModal_listJourneyTemplates = graphql(`
	query journeyCreateModal_listJourneyTemplates {
		listJourney(filter: { isTemplate: { eq: true } }) {
			id
			name
			color
			description
		}
	}
`);

const createFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().nonempty(intl.formatMessage({ id: 'form.journey.error.invalid-name' })),
		website: z.string().optional(),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const createJourneyModalSchema = z.object({});

type Props = z.infer<typeof createJourneyModalSchema>;

const CreateJourneyModal: FC<Props> = () => {
	const intl = useIntl();
	const theme = useTheme();
	const navigate = useNavigate();
	const { closeModal } = useModalContext();
	const CHOOSE_TEMPLATE = 'choose-template';
	const [selectedTemplateId, setSelectedTemplateId] = useState<string>(CHOOSE_TEMPLATE);
	const [, setTemplateName] = useState<string | null>(null);
	const { data: journeyTemplates } = useApiQuery(journeyCreateModal_listJourneyTemplates);
	const { openSnackbar } = useSnackbar();

	const form = useTypedForm({
		schema: createFormSchema(intl),
	});

	const cancelDialog = () => {
		closeModal();
	};

	// sync form initial values
	useEffect(() => {
		form.reset({
			name: '',
		});
	}, [form, form.reset]);

	const { mutate: createJourneyFromTemplate } = useApiMutation(journey_createJourneyFromTemplate);
	const { mutate: createJourney } = useApiMutation(journey_createJourney);

	const onSubmit = (values: FormValues) => {
		setSelectedTemplateId('');
		if (journeyTemplates?.listJourney.find((journey) => journey.id === selectedTemplateId)) {
			createJourneyFromTemplate(
				{
					name: values.name,
					journeyId: selectedTemplateId,
				},
				{
					onSuccess: (result) => {
						const ok = result.createJourneyDeepClone.ok || false;
						const newJourneyId = result.createJourneyDeepClone.node?.id;

						mutationResponseHandler({
							result: ok,
							toastMessages: {
								success: intl.formatMessage({ id: 'common.journey.create.success' }),
								error: intl.formatMessage({ id: 'common.journey.create.error' }),
							},
							errorMessage: result.createJourneyDeepClone.errorMessage,
							callback: () => {
								cancelDialog();
								if (newJourneyId != null) {
									navigate(`journeys/${newJourneyId}/activity-map`);
								}
							},
							openSnackbar,
						});
					},
				},
			);
		} else {
			createJourney(
				{
					name: values.name,
				},
				{
					onSuccess: (result) => {
						const ok = result.createJourney.ok || false;
						const newJourneyId = result.createJourney.node?.id;

						mutationResponseHandler({
							result: ok,
							toastMessages: {
								success: intl.formatMessage({ id: 'common.journey.create.success' }),
								error: intl.formatMessage({ id: 'common.journey.create.error' }),
							},
							errorMessage: result.createJourney.errorMessage,
							callback: () => {
								cancelDialog();
								if (newJourneyId != null) {
									navigate(`journeys/${newJourneyId}/activity-map`);
								}
							},
							openSnackbar,
						});
					},
				},
			);
		}
	};

	return (
		<form noValidate onSubmit={form.handleSubmit(onSubmit)} style={{ display: 'grid', height: '100%' }}>
			<Stack direction={'column'} gap={4} height={'100%'}>
				<Stack direction={'row'} padding={3} justifyContent={'space-between'}>
					<IconButton onClick={cancelDialog}>
						<ArrowBack htmlColor={theme.palette.grey[700]} />
					</IconButton>
					<IconButton onClick={cancelDialog}>
						<Close htmlColor={theme.palette.grey[700]} />
					</IconButton>
				</Stack>
				<Stack direction={{ xs: 'column', sm: 'row' }} height={'100%'}>
					<Stack gap={4} marginBottom={5} maxWidth={550} width={'100%'} padding={{ xs: 3, sm: 4 }}>
						<Stack direction={'row'} justifyContent={'center'}>
							<Stack direction={'column'} gap={0.5} maxWidth={{ sm: '450px' }} width={'100%'}>
								<Typography variant={'h1'} fontSize={30} fontWeight={700}>
									<FormattedMessage id={'form.journey.create.title'} />
								</Typography>
							</Stack>
						</Stack>
						<Stack direction={'row'} justifyContent={'center'}>
							<Stack maxWidth={{ sm: '450px' }} width={'100%'} gap={3}>
								<Stack gap={1}>
									<InputLabel htmlFor={'name'}>
										<Typography variant={'h6'} fontWeight={'500'}>
											<FormattedMessage id={'form.journey.create.name.label'} />
										</Typography>
									</InputLabel>
									<Stack>
										<OutlinedInput
											{...form.register('name')}
											placeholder={intl.formatMessage({ id: 'form.journey.create.name.placeholder' })}
											id={'name'}
										/>
										{form.formState.errors.name != null && <FormHelperText error>{form.formState.errors.name.message}</FormHelperText>}
									</Stack>
								</Stack>
								{/* TODO: Not available yet */}
								{/* <Stack gap={1}>
									<InputLabel htmlFor={'website'}>
										<Typography variant={'h6'} fontWeight={'500'}>
											<FormattedMessage id={'form.journey.create.website.label'} />
										</Typography>
									</InputLabel>
									<Stack>
										<OutlinedInput
											{...form.register('website')}
											id={'website'}
											sx={{ paddingLeft: 10, overflow: 'hidden' }}
											startAdornment={
												<Box
													display={'flex'}
													paddingX={1.5}
													position={'absolute'}
													left={0}
													height={'100%'}
													alignItems={'center'}
													border={`1px solid ${theme.palette.grey[300]}`}
													sx={{ backgroundColor: theme.palette.grey[50] }}
												>
													<Typography color={theme.palette.grey[500]}>
														<FormattedMessage id={'form.journey.create.website.adornment'} />
													</Typography>
												</Box>
											}
										/>
										{form.formState.errors.website != null && (
											<FormHelperText error>{form.formState.errors.website.message}</FormHelperText>
										)}
									</Stack>
								</Stack> */}
							</Stack>
						</Stack>
						<Stack direction={'row'} justifyContent={'center'}>
							<Stack maxWidth={{ sm: '450px' }} width={'100%'}>
								<Button
									type={'submit'}
									variant={'contained'}
									sx={{ backgroundColor: theme.palette.primary[400] }}
									fullWidth
									size={'large'}
									disabled={form.formState.isSubmitting || !selectedTemplateId || selectedTemplateId === CHOOSE_TEMPLATE}
								>
									<Typography variant={'h6'} fontWeight={'500'}>
										<FormattedMessage id={'form.journey.create.create-button'} />
									</Typography>
								</Button>
							</Stack>
						</Stack>
					</Stack>
					<CreateJourneyTemplates
						setSelectedJourenyId={setSelectedTemplateId}
						setTemplateName={setTemplateName}
						selectedTemplateId={selectedTemplateId}
					/>
				</Stack>
			</Stack>
		</form>
	);
};

export { CreateJourneyModal, createJourneyModalSchema };
